import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LanguageIcon from "@mui/icons-material/Language";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { getPaymentsOverview } from "../../DAL/Payments/Payments";

const cardSx = {
  p: 3,
  backgroundColor: "#ffffff",
  border: "1px solid rgba(26,26,26,0.09)",
  borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const thSx = {
  color: "#6b6b6b", fontWeight: 600, fontSize: "11px",
  textTransform: "uppercase", letterSpacing: "0.4px",
  backgroundColor: "#F2EDE4", borderBottom: "1px solid rgba(26,26,26,0.09)",
};

const StatCard = ({ label, value, subValue, icon, color = "#c01e2e" }) => (
  <Paper sx={{ ...cardSx, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
    <Box>
      <Typography sx={{ color: "#aaa", fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1 }}>{label}</Typography>
      <Typography sx={{ color: "#1a1a1a", fontSize: "26px", fontWeight: 700 }}>{value}</Typography>
      {subValue && <Typography sx={{ color: "#aaa", fontSize: "12px", mt: 0.5 }}>{subValue}</Typography>}
    </Box>
    <Box sx={{ width: 44, height: 44, borderRadius: "11px", backgroundColor: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
    </Box>
  </Paper>
);

const sourceChip = (source) => {
  if (source === "app") return <Chip label="App" size="small" sx={{ backgroundColor: "#eef2ff", color: "#4f46e5", fontWeight: 600, fontSize: "11px" }} />;
  if (source === "webhook") return <Chip label="Webhook" size="small" sx={{ backgroundColor: "#fef3c7", color: "#92400e", fontWeight: 600, fontSize: "11px" }} />;
  return <Chip label="Web" size="small" sx={{ backgroundColor: "#f0fdf4", color: "#15803d", fontWeight: 600, fontSize: "11px" }} />;
};

const typeLabel = (type) => {
  const map = {
    subscription: "Subscription",
    token_purchase: "Token Purchase",
    explanation_credits: "Credits",
    exam_insurance: "Exam Insurance",
    pass_guarantee: "Pass Guarantee",
    flight_school_seat: "School Seat",
    flight_school_ai_usage: "School AI",
    ground_instructor_credits: "Ground Instructor",
    other: "Other",
  };
  return map[type] || type;
};

export default function Payments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");

  useEffect(() => {
    setLoading(true);
    getPaymentsOverview(parseInt(period))
      .then((res) => {
        if (res.success) setData(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress sx={{ color: "#c01e2e" }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Failed to load payment data</Typography>
      </Box>
    );
  }

  const fmt = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1a1a1a" }}>Payments</Typography>
          <Typography sx={{ fontSize: "13px", color: "#aaa", mt: 0.5 }}>Revenue across all platforms</Typography>
        </Box>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, v) => v && setPeriod(v)}
          size="small"
          sx={{ "& .MuiToggleButton-root": { fontSize: "12px", fontWeight: 600, textTransform: "none", px: 2 }, "& .Mui-selected": { backgroundColor: "#c01e2e !important", color: "#fff !important" } }}
        >
          <ToggleButton value="7">7d</ToggleButton>
          <ToggleButton value="30">30d</ToggleButton>
          <ToggleButton value="90">90d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
        <StatCard label="Total Revenue" value={fmt(data.totalRevenue)} subValue={`${data.totalTransactions} transactions`} icon={<TrendingUpIcon />} />
        <StatCard label="Web Revenue" value={fmt(data.webRevenue)} icon={<LanguageIcon />} color="#15803d" />
        <StatCard label="App Revenue" value={fmt(data.appRevenue)} icon={<PhoneIphoneIcon />} color="#4f46e5" />
        <StatCard label="Active Subscribers" value={data.activeSubscribers} icon={<PeopleIcon />} color="#d97706" />
        <StatCard
          label="Stripe Revenue (Web Only)"
          value={data.stripeRevenue?.amount != null ? fmt(data.stripeRevenue.amount) : "—"}
          subValue={data.stripeRevenue?.amount != null ? `${data.stripeRevenue.count} charges · ground-truth check, excludes Apple/IAP` : "Unavailable"}
          icon={<AccountBalanceIcon />}
          color="#8b5cf6"
        />
      </Box>

      {/* Revenue by type */}
      {data.revenueByType?.length > 0 && (
        <Paper sx={{ ...cardSx, mb: 3 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1a1a1a", mb: 2 }}>Revenue by Type</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {data.revenueByType.map((r) => (
              <Box key={r.type} sx={{ flex: "1 1 200px", p: 2, borderRadius: "10px", border: "1px solid rgba(26,26,26,0.08)", backgroundColor: "#faf7f2" }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.3px" }}>{typeLabel(r.type)}</Typography>
                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a", mt: 0.5 }}>{fmt(r.total)}</Typography>
                <Typography sx={{ fontSize: "11px", color: "#aaa" }}>{r.count} transactions</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Recent transactions */}
      <Paper sx={{ ...cardSx }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1a1a1a", mb: 2 }}>Recent Transactions</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Date</TableCell>
                <TableCell sx={thSx}>User</TableCell>
                <TableCell sx={thSx}>Type</TableCell>
                <TableCell sx={thSx}>Amount</TableCell>
                <TableCell sx={thSx}>Source</TableCell>
                <TableCell sx={thSx}>Plan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.recentTransactions.map((t) => (
                <TableRow key={t.id} hover sx={{ "&:hover": { backgroundColor: "#faf7f2" } }}>
                  <TableCell sx={{ fontSize: "13px", color: "#6b6b6b" }}>
                    {new Date(t.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a" }}>{t.userName || "Unknown"}</Typography>
                    <Typography sx={{ fontSize: "11px", color: "#aaa" }}>{t.email}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: "12px", color: "#1a1a1a" }}>{typeLabel(t.type)}</TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 700, color: "#1a1a1a" }}>{fmt(t.amount)}</TableCell>
                  <TableCell>{sourceChip(t.source)}</TableCell>
                  <TableCell sx={{ fontSize: "12px", color: "#6b6b6b" }}>{t.plan || "—"}</TableCell>
                </TableRow>
              ))}
              {data.recentTransactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "#aaa" }}>No transactions in this period</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
